import {
  ICustomReportFilter,
  PatientStatus,
  ReportBuilderDataSourceId,
  ReportFilterComparison,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { titlecase } from '@principle-theorem/shared';
import { CustomReportFilter } from '../../models/custom-report-filters';
import { FactTables } from '../../models/fact-tables';
import { toMeasureBuilderData } from '../../models/measure-properties';
import { ReportBuilderColumns } from '../../report-builder-columns';
import { ChartFilterDefinition } from '../charts/chart-filter-definition';
import { ReportBuilderCharts } from '../charts/report-builder-charts';
import { ReportBuilderCommonCharts } from '../charts/report-builder-common-charts';
import { ReportBuilderDataSourceInstance } from '../report-builder-data-source-instance';
import { ReportBuilderGroups } from '../report-builder-groups';
import { ReportBuilderScopeDataAccessors } from '../report-builder-scope-data-accessors';

export class PatientsDataSource extends ReportBuilderDataSourceInstance {
  override readonly id = ReportBuilderDataSourceId.Patients;
  override readonly name = 'Patients';
  override readonly description = `One result per patient within Principle. This includes information about the patient's finances.`;
  override readonly factTable = FactTables.patientEvent.latestEvent;
  override readonly rowItemName = 'Patient';

  override readonly queryableTimestamps = [
    { property: this.factTable.patient.createdAt },
    { property: this.factTable.patient.lastVisitDate },
    { property: this.factTable.patient.nextVisitDate },
    { property: this.factTable.patient.recall.nextRecallDue.dueAt },
  ];

  scopes = [TemplateScope.Patient];
  scopeDataAccessor = ReportBuilderScopeDataAccessors.build([
    this.factTable.patient,
  ]);

  override readonly groupByOptions = [
    ReportBuilderGroups.toGroup('Common', [
      ReportBuilderGroups.toOption(this.factTable.brand.name),
      ReportBuilderGroups.toOption(this.factTable.count),
    ]),
    ReportBuilderGroups.patient(this.factTable.patient),
  ];

  override readonly availableColumns = [
    ReportBuilderColumns.toColumnGroup('Patient', [
      ...ReportBuilderColumns.patient(this.factTable.patient),
      this.factTable.patientLink,
    ]),
  ];

  override readonly defaultColumns = ReportBuilderColumns.toReferences([
    this.factTable.patient.name,
    this.factTable.patient.mobileNumber,
    this.factTable.patient.email,
    this.factTable.patient.age,
    this.factTable.patient.gender,
    this.factTable.patient.referrer,
    this.factTable.patient.tags,
    this.factTable.patient.creditRemaining,
    this.factTable.patient.paymentsReceived,
    this.factTable.patient.outstanding,
  ]);

  override staticFilters: ICustomReportFilter[] = [
    CustomReportFilter.init({
      id: this.factTable.patient.status.metadata.id,
      comparison: ReportFilterComparison.IsEqualTo,
      values: [
        {
          label: titlecase(PatientStatus.Active),
          value: PatientStatus.Active,
        },
      ],
    }),
  ];

  override readonly availableCharts = [
    ReportBuilderCharts.predefined(
      'overview.patient.count',
      this.factTable.patient.name,
      ChartFilterDefinition.numberSummary(
        'Total Patients',
        this.factTable.patient.name,
        [toMeasureBuilderData(this.factTable.count)]
      )
    ),
    ...ReportBuilderCommonCharts.patient(
      this.factTable.patient,
      this.factTable.count
    ),
  ];
}
